import { useState } from 'react'
import {
  BACKGROUND_PRESETS,
  fontStack,
  isLightBackground,
  type BoardTemplateContent,
  type BoardTheme,
  type OrgDesign,
} from '@praesto/shared'
import { ColorPicker } from './ColorPicker.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'
import { FontSelect, useOrgFonts } from './FontLibrary.js'
import { BoardTemplateCard, useBoardTemplates } from './BoardTemplatesDialog.js'

/** Platz der Akzentfarbe in der Palette — sie färbt Rahmen und Hervorhebungen. */
const ACCENT_SLOT = 2

export interface BoardDraft {
  title: string
  background: string
  theme: BoardTheme
  /** Das gewählte Gerüst; `null` heißt: leer beginnen. */
  template: BoardTemplateContent | null
}

/**
 * Das Fenster, das beim Anlegen eines Boards erscheint.
 *
 * Es fragt nur, was man später ungern nachzieht: Gerüst, Name, Untergrund,
 * Akzentfarbe, Schriften. Alles ist vorbelegt — und zwar mit dem Standard-Design
 * der Organisation, damit die Hausgestaltung nicht bei jedem Board neu
 * zusammengesucht wird. Wer nichts festlegen will, drückt Eingabe und ist im
 * Board. Der Rest des Designs bleibt im Design-Fenster; hier steht, was die
 * Anmutung sofort bestimmt.
 *
 * Das Gerüst steht oben und nicht unten: Es ist die einzige Wahl, die man
 * hinterher nur mühsam nachholt — Farben ändert man in zwei Klicks, sieben
 * Frames legt man nicht noch einmal von Hand.
 */
export function NewBoardDialog({
  orgId,
  design,
  onCreate,
  onClose,
}: {
  orgId: string
  design: OrgDesign
  onCreate: (draft: BoardDraft) => Promise<void>
  onClose: () => void
}): React.ReactElement {
  const t = useText()
  const [title, setTitle] = useState('')
  const [background, setBackground] = useState(design.background)
  const [theme, setTheme] = useState<BoardTheme>(design.theme)
  const [fonts] = useOrgFonts(orgId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { templates } = useBoardTemplates(orgId)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const template = templates?.find((entry) => entry.id === templateId) ?? null

  const accent = theme.palette[ACCENT_SLOT] ?? design.theme.palette[ACCENT_SLOT] ?? '#e8622e'
  const light = isLightBackground(background)

  function setAccent(color: string): void {
    const palette = [...theme.palette]
    palette[ACCENT_SLOT] = color
    // Der Untergrund gehört ebenfalls in die Palette, damit Flächen, die sich
    // einfügen sollen, ohne Umweg zu treffen sind.
    palette[1] = background
    setTheme({ ...theme, palette })
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await onCreate({
        title: title.trim() || t('newBoard.defaultTitle'),
        background,
        theme,
        template: template?.content ?? null,
      })
    } catch {
      setError(t('newBoard.failed'))
      setBusy(false)
    }
  }

  return (
    <Dialog title={t('newBoard.title')} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <span>{t('newBoard.skeleton')}</span>
          {/*
            Die leere Fläche steht als eigene Kachel und nicht als „nichts
            auswählen": Sie ist eine Wahl wie jede andere, und wer sie trifft,
            soll sie auch angewählt sehen.
          */}
          <ul className="template-grid is-strip">
            <li>
              <button
                type="button"
                className={`template-card${templateId === null ? ' is-selected' : ''}`}
                onClick={() => setTemplateId(null)}
                aria-pressed={templateId === null}
              >
                <span className="template-sketch board-template-sketch is-empty" aria-hidden="true" />
                <span className="template-name">{t('newBoard.empty')}</span>
                <span className="template-meta muted small">{t('newBoard.emptyMeta')}</span>
              </button>
            </li>
            {/*
              Ohne Gruppenüberschriften und in einer Reihe: Die Reichweite
              interessiert hier niemanden, und drei Abschnitte übereinander
              schöben den Knopf zum Anlegen unter den Bildrand.
            */}
            {(templates ?? []).map((entry) => (
              <li key={entry.id}>
                <BoardTemplateCard
                  template={entry}
                  selected={templateId === entry.id}
                  onPick={(picked) => setTemplateId(picked.id === templateId ? null : picked.id)}
                />
              </li>
            ))}
          </ul>
          <small>{t('newBoard.skeletonHint')}</small>
        </div>

        <label className="field">
          <span>{t('common.name')}</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t('newBoard.titlePlaceholder')}
            autoFocus
            maxLength={200}
          />
          <small>{t('newBoard.titleHint')}</small>
        </label>

        <div className="field">
          <span>{t('theme.backgroundLabel')}</span>
          <span className="swatch-row">
            {BACKGROUND_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={`swatch${background === preset.value ? ' is-selected' : ''}`}
                style={{ backgroundColor: preset.value }}
                title={t(preset.name)}
                aria-label={t(preset.name)}
                aria-pressed={background === preset.value}
                onClick={() => setBackground(preset.value)}
              />
            ))}
            <ColorPicker
              value={background}
              onChange={setBackground}
              palette={theme.palette}
              title={t('color.custom')}
            />
          </span>
        </div>

        <div className="field">
          <span>{t('newBoard.accent')}</span>
          <span className="theme-row">
            <ColorPicker
              value={accent}
              onChange={setAccent}
              palette={theme.palette}
              title={t('newBoard.accent')}
            />
            <span className="muted small">{t('newBoard.accentHint')}</span>
          </span>
        </div>

        <FontSelect
          label={t('theme.headings')}
          value={theme.headingFont}
          fonts={fonts}
          onChange={(headingFont) => setTheme({ ...theme, headingFont })}
        />
        <FontSelect
          label={t('theme.body')}
          value={theme.bodyFont}
          fonts={fonts}
          onChange={(bodyFont) => setTheme({ ...theme, bodyFont })}
        />

        {/* Nicht dekorativ: Untergrund, Akzent und beide Schriften zusammen —
            genau das, was man sonst erst nach dem Anlegen sieht. */}
        <div
          className={`board-preview${light ? ' is-light' : ''}`}
          style={{ backgroundColor: background }}
          aria-hidden="true"
        >
          <span className="board-preview-frame" style={{ borderColor: accent }}>
            <span className="board-preview-heading" style={{ fontFamily: fontStack(theme.headingFont) }}>
              {title.trim() || t('newBoard.previewHeading')}
            </span>
            <span className="board-preview-body" style={{ fontFamily: fontStack(theme.bodyFont) }}>
              {t('newBoard.previewBody')}
            </span>
          </span>
        </div>

        {error && <p className="form-error">{error}</p>}

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('newBoard.creating') : t('newBoard.create')}
        </button>
      </form>
    </Dialog>
  )
}
